/**
 * P3-38: FixCryochamberBtn —— 修复用过的低温舱。
 *
 * 对齐 `js/time.js:1753-1869`：
 * - 使用 `FixCryochamberBtnController`
 * - onClick 必须将原生 MouseEvent（含 shiftKey/ctrlKey/metaKey）传给 controller.buyItem
 * - hasResourceHover: true
 */
import { useMemo, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";
import { useButtonModel, shallowSnapEq, makeModernTooltip, type ModernButtonController } from "../bonfire/useButtonModel";

interface FixCryochamberSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
}

function toSnapshot(model: Record<string, unknown>): FixCryochamberSnapshot {
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: Boolean(model.visible),
    enabled: Boolean(model.enabled),
  };
}

const CONTROLLER_OPTS = {
  name: "time.fixCryochambers.label",
  description: "time.fixCryochambers.desc",
};

export default function FixCryochamberBtn() {
  const controller = useMemo<ModernButtonController | null>(() => {
    const ControllerClass = (
      window as unknown as {
        classes?: { ui?: { time?: { FixCryochamberBtnController?: new (game: GamePage) => ModernButtonController } } };
      }
    ).classes?.ui?.time?.FixCryochamberBtnController;
    const game = getGame();
    if (typeof ControllerClass !== "function" || !game) {
      return null;
    }
    return new ControllerClass(game);
  }, []);

  const { snap, modelRef } = useButtonModel(controller, () => CONTROLLER_OPTS, toSnapshot, shallowSnapEq);

  const getTooltip = makeModernTooltip(() => controller, () => CONTROLLER_OPTS);

  if (!snap || !snap.visible) {
    return null;
  }

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      getTooltip={getTooltip}
      onClick={(event: MouseEvent) => {
        controller?.buyItem?.(modelRef.current, event);
        getGame()?.render();
      }}
    />
  );
}
